"use client";

import { c as _c } from "react/compiler-runtime";
import { ScrollShadow } from "../ui/scroll-shadow";
import { type ReactNode, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FiTrash } from "react-icons/fi";
import { RiFileCopyLine, RiLoopLeftLine } from "react-icons/ri";
import DeleteConfirmationModal from "@/components/delete-confirmation-modal";
import { Button, Card, Label } from "@/components/ui";
import IdDisplay from "@/components/ui/id-display";
import { cx } from "@/lib/tailwind-utils";
import { useCarePlanContext } from "./care-plan";
import { getBranchStructureLayoutKey, scheduleScrollCarePlanCanvasToCenterX } from "./care-plan-canvas-scroll";
import { CycleCreationConfig } from "./cycle-creation-config";
import { CycleProgressList } from "./cycle-progress-list";
import { InlineNameInput } from "./inline-name-input";
import { StepDetail } from "./step-detail";
import { CarePlanTagPicker } from "./tag-picker";
import type { CarePlanContextType } from "./types";
import { useCanvasZoom } from "./use-canvas-zoom";
import { ZoomControls } from "./zoom-controls";
interface CarePlanEditorLayoutProps {
  children?: ReactNode;
  footer?: ReactNode;
  leftPanel?: ReactNode;
  rightPanel?: ReactNode;
}
export function CarePlanEditorLayout(t0) {
  const $ = _c(56);
  const {
    children,
    footer,
    leftPanel,
    rightPanel
  } = t0;
  const {
    editor,
    state: t1,
    cycles: t2,
    sequenceCopy: t3,
    actions: t4
  } = useCarePlanContext();
  const {
    steps,
    selectedStep
  } = t1;
  const {
    openCreateCycle,
    cycleCreation,
    cancelCycleCreation,
    confirmCycleCreation
  } = t2;
  const {
    state: sequenceCopy,
    apply: applySequenceCopy,
    cancel: cancelSequenceCopy
  } = t3;
  const {
    selectStep
  } = t4;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const scrollContainerRef = useRef(null);
  const canvasRef = useRef(null);
  let t5;
  if ($[0] !== steps) {
    t5 = getBranchStructureLayoutKey(steps);
    $[0] = steps;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const branchLayoutKey = t5;
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      containerRef: scrollContainerRef,
      canvasRef
    };
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const {
    zoom,
    zoomIn,
    zoomOut,
    resetZoom,
    canvasStyle
  } = useCanvasZoom(t6);
  let t7;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      requestAnimationFrame(() => {
        scheduleScrollCarePlanCanvasToCenterX(scrollContainerRef.current, canvasRef.current);
      });
    };
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  let t8;
  if ($[4] !== branchLayoutKey) {
    t8 = [branchLayoutKey];
    $[4] = branchLayoutKey;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  useLayoutEffect(t7, t8);
  let t9;
  if ($[6] !== selectStep) {
    t9 = <div className="absolute top-0 left-0 bottom-0 w-[200vw]" onClick={() => selectStep(null)} />;
    $[6] = selectStep;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== children) {
    t10 = <div>{children}</div>;
    $[8] = children;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== canvasStyle || $[11] !== t10 || $[12] !== t9) {
    t11 = <div ref={scrollContainerRef} className="w-full h-full relative overflow-auto" data-care-plan-scroll-container={true}><div ref={canvasRef} className="min-h-full min-w-full w-max py-48 px-[80vw] flex justify-center" style={canvasStyle}>{t9}{t10}</div></div>;
    $[10] = canvasStyle;
    $[11] = t10;
    $[12] = t9;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== editor || $[15] !== footer || $[16] !== leftPanel || $[17] !== showDeleteModal) {
    t12 = leftPanel ?? (editor && <EditorPanel editor={editor} footer={footer} showDeleteModal={showDeleteModal} setShowDeleteModal={setShowDeleteModal} />);
    $[14] = editor;
    $[15] = footer;
    $[16] = leftPanel;
    $[17] = showDeleteModal;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== cancelCycleCreation || $[20] !== confirmCycleCreation || $[21] !== cycleCreation || $[22] !== leftPanel || $[23] !== openCreateCycle) {
    t13 = !leftPanel && <Card className="motion-preset-slide-left bg-background/70 backdrop-blur-sm self-start max-w-xs max-h-[calc(100%-20rem)] space-y-4 shadow-md flex pointer-events-auto w-full"><ScrollShadow.Root><ScrollShadow.Viewport>{cycleCreation?.phase === "configuring" && cycleCreation.startStepIndex !== null && cycleCreation.endStepIndex !== null ? <CycleCreationConfig stepCount={cycleCreation.endStepIndex - cycleCreation.startStepIndex + 1} onConfirm={confirmCycleCreation} onCancel={cancelCycleCreation} /> : <><CycleProgressList /><Button variant="ghost" size="sm" onClick={openCreateCycle} icon={<RiLoopLeftLine size={16} />} className="w-fit justify-start text-foreground-secondary">Create Cycle</Button></>}</ScrollShadow.Viewport><ScrollShadow.Top /><ScrollShadow.Bottom /></ScrollShadow.Root></Card>;
    $[19] = cancelCycleCreation;
    $[20] = confirmCycleCreation;
    $[21] = cycleCreation;
    $[22] = leftPanel;
    $[23] = openCreateCycle;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== footer) {
    t14 = footer && <div className="flex items-center gap-3 pt-1 pointer-events-auto [&_button]:shadow-md">{footer}</div>;
    $[25] = footer;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t12 || $[28] !== t13 || $[29] !== t14) {
    t15 = <div className="flex flex-col gap-4">{t12}{t13}{t14}</div>;
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== rightPanel || $[32] !== selectedStep || $[33] !== sequenceCopy) {
    t16 = rightPanel ?? (selectedStep && !sequenceCopy && <Card className="motion-preset-slide-left ml-auto bg-background/70 backdrop-blur-sm self-center max-w-sm max-h-[calc(100%-10rem)] w-full space-y-4 shadow-md flex pointer-events-auto"><ScrollShadow.Root><ScrollShadow.Viewport>{selectedStep && <StepDetail />}</ScrollShadow.Viewport><ScrollShadow.Top /><ScrollShadow.Bottom /></ScrollShadow.Root></Card>);
    $[31] = rightPanel;
    $[32] = selectedStep;
    $[33] = sequenceCopy;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t15 || $[36] !== t16) {
    t17 = <div className="absolute pointer-events-none w-full left-1/2 -translate-x-1/2 max-w-[95%] bottom-0 top-12 flex justify-between">{t15}{t16}</div>;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== resetZoom || $[39] !== zoom || $[40] !== zoomIn || $[41] !== zoomOut) {
    t18 = <div className="absolute top-24 right-4 -translate-y-1/2 pointer-events-auto z-10"><ZoomControls zoom={zoom} onZoomIn={zoomIn} onZoomOut={zoomOut} onResetZoom={resetZoom} /></div>;
    $[38] = resetZoom;
    $[39] = zoom;
    $[40] = zoomIn;
    $[41] = zoomOut;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== cancelCycleCreation || $[44] !== cycleCreation) {
    t19 = cycleCreation && cycleCreation.phase !== "configuring" && <div className="absolute top-12 left-1/2 -translate-x-1/2 z-10 pointer-events-auto"><div className="mt-3 flex items-center gap-3 rounded-lg border bg-background/90 backdrop-blur-sm shadow-md px-4 py-2.5"><div className="flex items-center gap-2"><RiLoopLeftLine size={16} className="text-primary" /><span className="text-body-strong text-foreground"><span>{cycleCreation.phase === "selecting-start" && "Click a step to set the cycle start"}</span><span>{cycleCreation.phase === "selecting-end" && "Now click a step to set the cycle end"}</span></span></div><Button variant="secondary" size="sm" onClick={cancelCycleCreation} className="text-foreground-secondary">Cancel</Button></div></div>;
    $[43] = cancelCycleCreation;
    $[44] = cycleCreation;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== applySequenceCopy || $[47] !== cancelSequenceCopy || $[48] !== sequenceCopy) {
    t20 = sequenceCopy && <div className="absolute top-12 left-1/2 -translate-x-1/2 z-10 pointer-events-auto"><div className="mt-3 flex items-center gap-3 rounded-lg border bg-background/90 backdrop-blur-sm shadow-md px-4 py-2.5"><div className="flex items-center gap-2"><RiFileCopyLine size={16} className="text-primary" /><span className="text-body-strong text-foreground">{sequenceCopy.targetStepIds.length === 0 ? "Click appointment steps to copy sequences to" : `${sequenceCopy.targetStepIds.length} step${sequenceCopy.targetStepIds.length === 1 ? "" : "s"} selected`}</span></div><Button variant="secondary" size="sm" onClick={cancelSequenceCopy} className="text-foreground-secondary">Cancel</Button><Button variant="primary" size="sm" onClick={applySequenceCopy} disabled={sequenceCopy.targetStepIds.length === 0}>Apply</Button></div></div>;
    $[46] = applySequenceCopy;
    $[47] = cancelSequenceCopy;
    $[48] = sequenceCopy;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== t11 || $[51] !== t17 || $[52] !== t18 || $[53] !== t19 || $[54] !== t20) {
    t21 = <>{t11}{t17}{t18}{t19}{t20}</>;
    $[50] = t11;
    $[51] = t17;
    $[52] = t18;
    $[53] = t19;
    $[54] = t20;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  return t21;
}
function EditorPanel(t0) {
  const $ = _c(42);
  const {
    editor,
    showDeleteModal,
    setShowDeleteModal
  } = t0;
  const {
    id,
    idLabel,
    name,
    onNameChange,
    namePlaceholder,
    nameAutoFocus,
    nameError,
    onDelete,
    isDeleting,
    descriptionInput,
    headerRight,
    selectedTagIds,
    onTagsChange,
    onTagSavingChange,
    tagError,
    tagErrorMessage
  } = editor;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cx("motion-slide-left max-w-xs w-full shadow-md transition-all duration-1000 bg-background/70 h-fit backdrop-blur-sm pointer-events-auto mx-0 group/editor-panel");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = !!nameError;
  let t3;
  if ($[1] !== name || $[2] !== nameAutoFocus || $[3] !== nameError || $[4] !== namePlaceholder || $[5] !== onNameChange || $[6] !== t2) {
    t3 = <InlineNameInput value={name} onChange={onNameChange} placeholder={namePlaceholder} autoFocus={nameAutoFocus} hasError={t2} errorMessage={nameError} className="w-full truncate text-foreground-secondary" />;
    $[1] = name;
    $[2] = nameAutoFocus;
    $[3] = nameError;
    $[4] = namePlaceholder;
    $[5] = onNameChange;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== id || $[9] !== idLabel) {
    t4 = id && <IdDisplay id={id} label={idLabel ?? "ID"} className="text-caption" />;
    $[8] = id;
    $[9] = idLabel;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <div>{t3}{t4}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== isDeleting || $[15] !== name || $[16] !== onDelete || $[17] !== setShowDeleteModal || $[18] !== showDeleteModal) {
    t6 = onDelete && <><DeleteConfirmationModal isOpen={showDeleteModal} setIsOpen={setShowDeleteModal} onConfirm={onDelete} title="Delete Care Plan" subtitle={`Are you sure you want to delete "${name}"? This action cannot be undone.`} isLoading={isDeleting} /><Button variant="ghost" size="sm" onClick={() => setShowDeleteModal(true)} className="text-status-error-text" icon={<FiTrash />}>Delete</Button></>;
    $[14] = isDeleting;
    $[15] = name;
    $[16] = onDelete;
    $[17] = setShowDeleteModal;
    $[18] = showDeleteModal;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t5 || $[21] !== t6) {
    t7 = <div className="flex items-start gap-3">{t5}{t6}</div>;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = cx("space-y-3", "transition-all duration-200", "opacity-0 max-h-0 overflow-hidden", "group-hover/editor-panel:opacity-100 group-hover/editor-panel:max-h-96 group-hover/editor-panel:overflow-visible group-hover/editor-panel:mt-3");
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== headerRight) {
    t9 = headerRight && <div className="flex gap-4">{headerRight}</div>;
    $[24] = headerRight;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Label className="text-caption text-foreground-secondary">Description</Label>;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== descriptionInput) {
    t11 = <div className="space-y-1">{t10}{descriptionInput}</div>;
    $[27] = descriptionInput;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== onTagSavingChange || $[30] !== onTagsChange || $[31] !== selectedTagIds || $[32] !== tagError || $[33] !== tagErrorMessage) {
    t12 = <CarePlanTagPicker selectedTagIds={selectedTagIds} onChange={onTagsChange} onSavingChange={onTagSavingChange} hasError={tagError} errorMessage={tagErrorMessage} />;
    $[29] = onTagSavingChange;
    $[30] = onTagsChange;
    $[31] = selectedTagIds;
    $[32] = tagError;
    $[33] = tagErrorMessage;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== t11 || $[36] !== t12 || $[37] !== t9) {
    t13 = <div className={t8}>{t9}{t11}{t12}</div>;
    $[35] = t11;
    $[36] = t12;
    $[37] = t9;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t13 || $[40] !== t7) {
    t14 = <Card className={t1}>{t7}{t13}</Card>;
    $[39] = t13;
    $[40] = t7;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  return t14;
}